import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InfoModal from "@/components/ui/InfoModal";
import apiClient from "@/services/apiClient";
import { Plus, Trash2 } from "lucide-react";
import {
  Form,
  Input,
  InputNumber,
  Checkbox,
  Button,
  Card,
  Select,
  AutoComplete,
  Space,
  Typography,
  Divider,
  Result,
} from "antd";
import Cron from "react-js-cron";
import { getLocalStorageWithExpiry, showSuccess, showApiError, clearAuthStorage } from "@/utils";
import PropTypes from "prop-types";
import "react-js-cron/dist/styles.css";
import "./CrawlConfig.css";
import { BACKEND_URL } from "@/config";

const { Title } = Typography;
const { Option } = Select;

const getEmptyCrawlRule = () => ({
  policy: "allow all",
  type: "regex",
  pattern: ".*",
});
const getEmptyDomain = () => ({
  url: "",
  sitemap_urls: [],
  seed_urls: [],
  crawl_rules: [getEmptyCrawlRule()],
});

const CrawlConfig = ({
  clientIdsWithConfig,
  closeModal,
  onSuccess,
  editConfig,
}) => {
  const { t, i18n } = useTranslation();
  const [maxCrawlDepth, setMaxCrawlDepth] = useState(1);
  const [sitemapDiscoveryDisabled, setSitemapDiscoveryDisabled] =
    useState(false);
  const [bypassRobotsTxt, setBypassRobotsTxt] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [rateLimitEnabled, setRateLimitEnabled] = useState(false);
  const [crawlHttpRateLimit, setCrawlHttpRateLimit] = useState(null);
  const [domain, setDomain] = useState(getEmptyDomain());
  const [crawlFrequency, setCrawlFrequency] = useState("0 0 * * 6");
  const [outputIndex, setOutputIndex] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [crawlType, setCrawlType] = useState("sitemap");
  const [clientId, setClientId] = useState("");
  const [domainUrls, setDomainUrls] = useState([]);
  const [domainUrlsLoading, setDomainUrlsLoading] = useState(false);
  const navigate = useNavigate();

  const isEdit = !!editConfig;

  useEffect(() => {
    if (isEdit && editConfig) {
      setMaxCrawlDepth(editConfig.config.max_crawl_depth);
      setSitemapDiscoveryDisabled(editConfig.config.sitemap_discovery_disabled);
      setBypassRobotsTxt(editConfig.config.bypass_robots_txt ?? false);
      setAiEnabled(editConfig.config.ai_enabled ?? false);
      setRateLimitEnabled(editConfig.config.rate_limit_enabled ?? false);
      setCrawlHttpRateLimit(editConfig.config.crawl_http_rate_limit ?? null);
      setCrawlFrequency(editConfig.crawl_frequency);
      setDomain(editConfig.config.domains[0]);
      setOutputIndex(editConfig.config.output_index ?? "");
      setCrawlType(
        (editConfig.config.domains[0]?.seed_urls?.length ?? 0) > 0
          ? "seed"
          : "sitemap",
      );
      setClientId(editConfig.client_id);
    }
  }, [isEdit, editConfig]);

  // Fetch domain URLs when clientId changes
  useEffect(() => {
    if (clientId) {
      if (!isEdit) {
        setDomain(getEmptyDomain());
      }
      setDomainUrls([]);
      setDomainUrlsLoading(true);
      apiClient
        .get(
          `${BACKEND_URL}/api/v1/client/${encodeURIComponent(clientId)}/domain-urls`,
        )
        .then((res) => {
          setDomainUrls(res.data?.domain_urls || []);
        })
        .catch(() => setDomainUrls([]))
        .finally(() => setDomainUrlsLoading(false));
    } else {
      setDomainUrls([]);
      setDomainUrlsLoading(false);
    }
  }, [clientId, isEdit]);

  // Auto-select first domain URL when needed
  useEffect(() => {
    if (
      domainUrls.length > 0 &&
      (!domain.url || !domainUrls.includes(domain.url))
    ) {
      setDomain((prev) => ({ ...prev, url: domainUrls[0] }));
    }
  }, [domainUrls, domain.url]);

  const projects = getLocalStorageWithExpiry("projects") || [];
  const availableProjects = isEdit
    ? projects.filter((proj) => proj.id === clientId)
    : projects.filter((proj) => !clientIdsWithConfig.includes(proj.id));

  // Auto-select first available client if not editing and clientId not set
  useEffect(() => {
    if (!isEdit && availableProjects.length > 0 && !clientId) {
      setClientId(() => {
        const client_id = getLocalStorageWithExpiry("client_id");
        if (client_id && availableProjects.some((p) => p.id === client_id)) {
          return client_id;
        }
        return availableProjects[0].id;
      });
    }
  }, [isEdit, availableProjects, clientId]);

  if (!isEdit && availableProjects.length === 0) {
    return (
      <Result
        status="info"
        title={t(
          "superAdmin.crawlConfig.noAvailableClientIds",
          "No Available Client IDs",
        )}
        subTitle={t(
          "superAdmin.crawlConfig.noAvailableClientIdsSubtitle",
          "All projects already have a crawl config. Please add a new project or remove an existing config to proceed.",
        )}
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            {t("common.refresh", "Refresh")}
          </Button>
        }
      />
    );
  }

  // Domain handlers
  const handleDomainChange = (field, value) => {
    setDomain({ ...domain, [field]: value });
  };

  const handleCrawlRuleChange = (ruleIdx, field, value) => {
    const newCrawlRules = [...domain.crawl_rules];
    if (field === "policy") {
      newCrawlRules[ruleIdx].policy = value;
      if (value === "allow all" || value === "deny all") {
        newCrawlRules[ruleIdx].type = "regex";
        newCrawlRules[ruleIdx].pattern = ".*";
      } else {
        newCrawlRules[ruleIdx].type = "contains";
        newCrawlRules[ruleIdx].pattern = "";
      }
    } else if (
      field === "pattern" &&
      newCrawlRules[ruleIdx].type === "begins"
    ) {
      // Ensure pattern starts with / for 'begins' type rules to match URL paths correctly
      let newPattern = value;
      if (newPattern && !newPattern.startsWith("/")) {
        newPattern = "/" + newPattern.replace(/^\/+/, "");
      }
      newCrawlRules[ruleIdx][field] = newPattern;
    } else {
      newCrawlRules[ruleIdx][field] = value;
    }
    setDomain({ ...domain, crawl_rules: newCrawlRules });
  };

  const addCrawlRule = () => {
    setDomain({
      ...domain,
      crawl_rules: [...domain.crawl_rules, getEmptyCrawlRule()],
    });
  };
  const removeCrawlRule = (ruleIdx) => {
    setDomain({
      ...domain,
      crawl_rules: domain.crawl_rules.filter((_, idx) => idx !== ruleIdx),
    });
  };

  const resetForm = () => {
    setDomain(getEmptyDomain());
    setMaxCrawlDepth(1);
    setSitemapDiscoveryDisabled(false);
    setBypassRobotsTxt(false);
    setAiEnabled(false);
    setRateLimitEnabled(false);
    setCrawlHttpRateLimit(null);
    setCrawlFrequency("0 0 * * 6");
    setOutputIndex("");
    setError(null);
  };

  const validateIndexName = (name) => {
    if (!name) return { isValid: true, message: null }; // Empty allowed (auto-generate)

    // Correct way to measure UTF-8 byte length
    const byteLength = new TextEncoder().encode(name).length;
    if (byteLength > 255)
      return {
        isValid: false,
        message: "Index name cannot be longer than 255 bytes.",
      };

    if (name === "." || name === "..")
      return { isValid: false, message: "Index name cannot be '.' or '..'." };
    if (/^[-_+.]/.test(name))
      return {
        isValid: false,
        message: "Index name cannot start with -, _, +, or .",
      };
    if (/[A-Z]/.test(name))
      return { isValid: false, message: "Index name must be lowercase only." };
    if (/[\\/*?"<>|# :,]/.test(name))
      return {
        isValid: false,
        message:
          'Index name contains forbidden characters (\\, /, *, ?, ", <, >, |, :, (space), ,, or #).',
      };
    return { isValid: true, message: null };
  };

  const validateUrlsList = (
    urls,
    fieldName,
    { requireAtLeastOne = true } = {},
  ) => {
    if (!Array.isArray(urls)) {
      return { isValid: false, message: `${fieldName} must be an array.` };
    }

    const nonEmptyUrls = [];

    for (const url of urls) {
      if (url == null) continue;
      if (typeof url !== "string") {
        return {
          isValid: false,
          message: `${fieldName} must contain only text URLs.`,
        };
      }

      const trimmedUrl = url.trim();
      if (trimmedUrl) {
        nonEmptyUrls.push(trimmedUrl);
      }
    }

    if (requireAtLeastOne && nonEmptyUrls.length === 0) {
      return {
        isValid: false,
        message: `${fieldName} must contain at least one valid URL.`,
      };
    }

    for (const trimmedUrl of nonEmptyUrls) {
      try {
        const parsedUrl = new URL(trimmedUrl);
        if (!["http:", "https:"].includes(parsedUrl.protocol)) {
          return {
            isValid: false,
            message: `Invalid URL in ${fieldName}: "${trimmedUrl}". Please ensure it uses http:// or https://.`,
          };
        }
      } catch (e) {
        return {
          isValid: false,
          message: `Invalid URL in ${fieldName}: "${trimmedUrl}". Please ensure it is a valid URL (e.g., https://example.com).`,
        };
      }
    }

    return { isValid: true, message: null };
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    // Validate Client ID
    if (!clientId) {
      setError("Please select a Client ID.");
      setLoading(false);
      return;
    }

    // Validate URL field
    let url = domain.url.trim();
    if (!url) {
      setError("Domain URL cannot be empty.");
      setLoading(false);
      return;
    }

    // Validate using URL class
    let domainUrlObj;
    try {
      domainUrlObj = new URL(url);
    } catch (e) {
      setError("Please enter a valid domain URL (e.g., https://example.com).");
      setLoading(false);
      return;
    }

    // Enforce strict hostname matching for Seed and Sitemap URLs
    const domainHostname = domainUrlObj.hostname.toLowerCase();

    if (crawlType === "sitemap") {
      const hasAnySitemapInput = domain.sitemap_urls.some((sitemapUrl) =>
        typeof sitemapUrl === "string"
          ? sitemapUrl.trim().length > 0
          : sitemapUrl != null,
      );

      if (sitemapDiscoveryDisabled || hasAnySitemapInput) {
        const sitemapValidation = validateUrlsList(
          domain.sitemap_urls,
          "Sitemap URLs",
          { requireAtLeastOne: sitemapDiscoveryDisabled },
        );
        if (!sitemapValidation.isValid) {
          setError(sitemapValidation.message);
          setLoading(false);
          return;
        }
      }

      for (const sitemapUrl of domain.sitemap_urls) {
        if (typeof sitemapUrl !== "string") {
          continue;
        }
        const trimmedUrl = sitemapUrl.trim();
        if (!trimmedUrl) continue;
        try {
          const sUrlObj = new URL(trimmedUrl);
          if (sUrlObj.hostname.toLowerCase() !== domainHostname) {
            setError(
              `Sitemap URL hostname mismatch: expected hostname \`${domainHostname}\`, but got \`${sUrlObj.hostname}\` for \`${trimmedUrl}\`.`,
            );
            setLoading(false);
            return;
          }
        } catch (e) {
          setError(`Invalid Sitemap URL: \`${trimmedUrl}\`.`);
          setLoading(false);
          return;
        }
      }
    }

    if (crawlType === "seed") {
      const seedValidation = validateUrlsList(domain.seed_urls, "Seed URLs");
      if (!seedValidation.isValid) {
        setError(seedValidation.message);
        setLoading(false);
        return;
      }

      for (const seedUrl of domain.seed_urls) {
        const trimmedUrl = seedUrl.trim();
        if (!trimmedUrl) continue;
        try {
          const sUrlObj = new URL(trimmedUrl);
          if (sUrlObj.hostname.toLowerCase() !== domainHostname) {
            setError(
              `Seed URL hostname mismatch: expected hostname \`${domainHostname}\` to match domain URL, but got \`${sUrlObj.hostname}\` for \`${trimmedUrl}\`.`,
            );
            setLoading(false);
            return;
          }
        } catch (e) {
          setError(`Invalid Seed URL: \`${trimmedUrl}\`.`);
          setLoading(false);
          return;
        }
      }
    }

    const indexValidation = validateIndexName(outputIndex);
    if (!indexValidation.isValid) {
      setError(indexValidation.message);
      setLoading(false);
      return;
    }

    if (rateLimitEnabled && crawlHttpRateLimit == null) {
      setError(
        "Please enter an HTTP crawl rate limit when rate limiting is enabled.",
      );
      setLoading(false);
      return;
    }

    // The backend API only accepts "allow" or "deny" as valid policy values.
    // In the frontend, we use "allow all"/"deny all" for user clarity,
    // so we map them to "allow"/"deny" before sending to the backend.
    const mappedCrawlRules = domain.crawl_rules.map((rule) => {
      if (rule.policy === "allow all") {
        return { ...rule, policy: "allow" };
      }
      if (rule.policy === "deny all") {
        return { ...rule, policy: "deny" };
      }
      return rule;
    });

    const config = {
      max_crawl_depth: Number(maxCrawlDepth),
      sitemap_discovery_disabled: sitemapDiscoveryDisabled,
      bypass_robots_txt: bypassRobotsTxt,
      ai_enabled: aiEnabled,
      rate_limit_enabled: rateLimitEnabled,
      crawl_http_rate_limit: rateLimitEnabled ? crawlHttpRateLimit : null,
      output_index: outputIndex || null,
      domains: [
        {
          ...domain,
          url,
          crawl_rules: mappedCrawlRules,
        },
      ],
    };

    let succeeded = false;
    try {
      const method = isEdit ? "put" : "post";
      const res = await apiClient({
        method,
        url: `${BACKEND_URL}/api/v1/content-ingestion/crawl/config/${encodeURIComponent(clientId)}`,
        data: { config, crawl_frequency: crawlFrequency },
      });
      if (!res.data.success) {
        const errMsg =
          res.data.detail ||
          res.data.message ||
          `Failed to ${isEdit ? "update" : "create"} crawl config`;
        setError(errMsg);
        showApiError(errMsg);
        return;
      }
      succeeded = true;
      showSuccess(
        res.data.message ||
          `Crawl config ${isEdit ? "updated" : "created"} successfully!`,
      );
      resetForm();
      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuthStorage();
        navigate("/login");
      }
      console.error(
        `Error ${isEdit ? "updating" : "creating"} crawl config:`,
        err,
      );
      const errMsg =
        err.response?.data?.detail ||
        `Error ${isEdit ? "updating" : "creating"} crawl config`;
      setError(errMsg);
      showApiError(errMsg);
    } finally {
      setLoading(false);
      if (succeeded) closeModal();
    }
  };

  const handleCrawlTypeChange = (val) => {
    setCrawlType(val);
    if (val === "sitemap") {
      setMaxCrawlDepth(1);
      setSitemapDiscoveryDisabled(false);
    } else if (val === "seed") {
      setMaxCrawlDepth(2);
      setSitemapDiscoveryDisabled(true);
    }
  };

  const handleRateLimitEnabledChange = (event) => {
    const enabled = event.target.checked;
    setRateLimitEnabled(enabled);
    if (!enabled) {
      setCrawlHttpRateLimit(null);
    }
  };

  return (
    <>
      <Form layout="vertical" onFinish={handleSubmit} disabled={loading}>
        <Card style={{ maxWidth: 900, margin: "16px auto" }}>
          <Form.Item
            label={
              <Space align="center">
                <span>{t("superAdmin.crawlConfig.clientId", "Client ID")}</span>
                <InfoModal
                  label="Client ID"
                  info={t(
                    "superAdmin.crawlConfig.clientIdInfo",
                    "Select the client ID for which to create the crawl config.",
                  )}
                />
              </Space>
            }
            required
          >
            {availableProjects.length > 1 ? (
              <Select
                onChange={setClientId}
                style={{ width: 300 }}
                disabled={loading}
                value={clientId}
              >
                {availableProjects.map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                style={{ width: 300 }}
                value={availableProjects[0]?.name}
                disabled
              />
            )}
          </Form.Item>
          <Form.Item
            label={
              <Space align="center">
                <span>
                  {t(
                    "superAdmin.crawlConfig.crawlFrequency",
                    "Crawl Frequency",
                  )}
                </span>
                <InfoModal
                  label="Crawl Frequency"
                  info={t(
                    "superAdmin.crawlConfig.crawlFrequencyInfo",
                    "Specify how often the crawl should run using a cron expression.",
                  )}
                />
              </Space>
            }
            required
          >
            <div className="cron-custom-wrapper">
              <Cron
                value={crawlFrequency}
                setValue={setCrawlFrequency}
                locale={i18n.language && i18n.language.startsWith("ja") ? "ja" : "en"}
                clearButton={true}
                humanizeLabels
                leadingZero
                period="day"
                style={{ width: "100%" }}
                disabled={loading}
              />
            </div>
          </Form.Item>
          <Form.Item
            label={
              <Space align="center">
                <span>{t("superAdmin.crawlConfig.domainUrl", "Domain URL")}</span>
                <InfoModal
                  label="Domain URL"
                  info={t(
                    "superAdmin.crawlConfig.domainUrlInfo",
                    "The base URL of the domain to crawl. Example: 'https://example.com'.",
                  )}
                />
              </Space>
            }
            required
          >
            <AutoComplete
              value={domain.url}
              onChange={(value) => handleDomainChange("url", value)}
              options={domainUrls.map((url) => ({ value: url }))}
              placeholder={
                domainUrlsLoading
                  ? t(
                      "superAdmin.crawlConfig.loadingDomainUrls",
                      "Loading domain URLs...",
                    )
                  : t(
                      "superAdmin.crawlConfig.selectOrEnterDomainUrl",
                      "Select or enter a domain URL",
                    )
              }
              disabled={loading || !clientId || domainUrlsLoading}
              loading={domainUrlsLoading}
              style={{ width: "100%" }}
              filterOption={(inputValue, option) =>
                option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                -1
              }
            />
          </Form.Item>
          <Form.Item
            label={
              <Space align="center">
                <span>
                  {t("superAdmin.crawlConfig.outputIndex", "Output Index")}
                </span>
                <InfoModal
                  label="Output Index"
                  info={t(
                    "superAdmin.crawlConfig.outputIndexInfo",
                    'The name of the index you wish to create. If left blank, it will be automatically generated. Index names must be lowercase, cannot start with -, _, +, or ., and cannot contain \\, /, *, ?, ", <, >, |, (space), ,, #, or :.',
                  )}
                />
              </Space>
            }
          >
            <Input
              value={outputIndex}
              onChange={(e) => setOutputIndex(e.target.value)}
              placeholder={t(
                "superAdmin.crawlConfig.leaveBlankForAutomatic",
                "Leave blank for automatic generation",
              )}
              disabled={loading}
            />
          </Form.Item>
          <Form.Item
            label={
              <Space align="center">
                <span>{t("superAdmin.crawlConfig.crawlType", "Crawl Type")}</span>

                <InfoModal
                  label="Crawl Type"
                  info={t(
                    "superAdmin.crawlConfig.crawlTypeInfo",
                    "Choose between Sitemap crawling or Seed crawling.",
                  )}
                />
              </Space>
            }
            required
          >
            <Select
              value={crawlType}
              onChange={handleCrawlTypeChange}
              style={{ width: 200 }}
              disabled={loading}
            >
              <Option value="sitemap">
                {t("superAdmin.crawlConfig.sitemapCrawling", "Sitemap Crawling")}
              </Option>
              <Option value="seed">
                {t("superAdmin.crawlConfig.seedCrawling", "Seed Crawling")}
              </Option>
            </Select>
          </Form.Item>
          {/* Sitemap URLs and Discovery Disabled (only for sitemap crawling) */}
          {crawlType === "sitemap" && (
            <>
              <Form.Item
                label={
                  <Space align="center">
                    <span>
                      {t("superAdmin.crawlConfig.sitemapUrls", "Sitemap URLs")}
                    </span>
                    <InfoModal
                      label="Sitemap URLs"
                      info={t(
                        "superAdmin.crawlConfig.sitemapUrlsInfo",
                        "Comma-separated list of sitemap URLs for the domain. Example: 'https://example.com/sitemap.xml'.",
                      )}
                    />
                    <span
                      style={{ color: "#888", fontSize: 12, marginLeft: 8 }}
                    >
                      {t(
                        "superAdmin.crawlConfig.leaveBlankForSitemapDiscovery",
                        "Leave blank for automatic sitemap discovery",
                      )}
                    </span>
                  </Space>
                }
              >
                <Input
                  value={domain.sitemap_urls.join(",")}
                  onChange={(e) =>
                    handleDomainChange(
                      "sitemap_urls",
                      e.target.value.split(",").map((s) => s.trim()),
                    )
                  }
                  placeholder="https://example.com/sitemap.xml"
                  disabled={loading}
                />
              </Form.Item>
              <Form.Item
                label={
                  <Space align="center">
                    <span>
                      {t(
                        "superAdmin.crawlConfig.sitemapDiscoveryDisabled",
                        "Sitemap Discovery Disabled",
                      )}
                    </span>
                    <InfoModal
                      label="Sitemap Discovery Disabled"
                      info={t(
                        "superAdmin.crawlConfig.sitemapDiscoveryDisabledInfo",
                        "If checked, the crawler will NOT attempt to automatically discover sitemaps from the domain.",
                      )}
                    />
                  </Space>
                }
              >
                <Checkbox
                  checked={sitemapDiscoveryDisabled}
                  onChange={(e) =>
                    setSitemapDiscoveryDisabled(e.target.checked)
                  }
                  disabled={crawlType === "seed" || loading}
                >
                  {t(
                    "superAdmin.crawlConfig.disableAutomaticSitemapDiscovery",
                    "Disable automatic sitemap discovery",
                  )}
                </Checkbox>
              </Form.Item>
            </>
          )}
          {/* Seed URLs (only for seed crawling) */}
          {crawlType === "seed" && (
            <Form.Item
              label={
                <Space align="center">
                  <span>{t("superAdmin.crawlConfig.seedUrls", "Seed URLs")}</span>
                  <InfoModal
                    label="Seed URLs"
                    info={t(
                      "superAdmin.crawlConfig.seedUrlsInfo",
                      "Comma-separated list of starting URLs for the crawler. Example: 'https://example.com/page1'.",
                    )}
                  />
                </Space>
              }
            >
              <Input
                value={domain.seed_urls.join(",")}
                onChange={(e) =>
                  handleDomainChange(
                    "seed_urls",
                    e.target.value.split(",").map((s) => s.trim()),
                  )
                }
                placeholder="https://example.com/page1"
                disabled={loading}
              />
            </Form.Item>
          )}
          <Form.Item
            label={
              <Space align="center">
                <span>
                  {t("superAdmin.crawlConfig.maxCrawlDepth", "Max Crawl Depth")}
                </span>
                <InfoModal
                  label="Max Crawl Depth"
                  info={t(
                    "superAdmin.crawlConfig.maxCrawlDepthInfo",
                    "The maximum number of link levels the crawler will follow from the starting URLs. A depth of 1 means only the starting URLs are crawled; 2 means starting URLs and their direct links, etc.",
                  )}
                />
              </Space>
            }
            required
          >
            <InputNumber
              min={1}
              value={maxCrawlDepth}
              onChange={setMaxCrawlDepth}
              style={{ width: "100%" }}
              disabled={crawlType === "sitemap" || loading}
            />
          </Form.Item>
          <Form.Item
            label={
              <Space align="center">
                <span>
                  {t(
                    "superAdmin.crawlConfig.robotsTxtHandling",
                    "Robots.txt Handling",
                  )}
                </span>
                <InfoModal
                  label="Robots.txt Handling"
                  info={t(
                    "superAdmin.crawlConfig.robotsTxtHandlingInfo",
                    "If enabled, the crawler will bypass robots.txt restrictions for this crawl configuration.",
                  )}
                />
              </Space>
            }
          >
            <Checkbox
              checked={bypassRobotsTxt}
              onChange={(e) => setBypassRobotsTxt(e.target.checked)}
              disabled={loading}
            >
              {t("superAdmin.crawlConfig.bypassRobotsTxt", "Bypass robots.txt")}
            </Checkbox>
          </Form.Item>
          <Form.Item
            label={
              <Space align="center">
                <span>{t("superAdmin.crawlConfig.aiEnabled", "AI Enabled")}</span>
                <InfoModal
                  label="AI Enabled"
                  info={t(
                    "superAdmin.crawlConfig.aiEnabledInfo",
                    "If enabled, AI will be used to enhance the content ingestion process.",
                  )}
                />
              </Space>
            }
          >
            <Checkbox
              checked={aiEnabled}
              onChange={(e) => setAiEnabled(e.target.checked)}
              disabled={loading}
            >
              {t("superAdmin.crawlConfig.enableAiIngestion", "Enable AI Ingestion")}
            </Checkbox>
          </Form.Item>
          <Form.Item
            label={
              <Space align="center">
                <span>
                  {t(
                    "superAdmin.crawlConfig.httpRateLimiting",
                    "HTTP Rate Limiting",
                  )}
                </span>
                <InfoModal
                  label="HTTP Rate Limiting"
                  info={t(
                    "superAdmin.crawlConfig.httpRateLimitingInfo",
                    "Enable a crawl request rate limit and specify the maximum HTTP request rate when needed.",
                  )}
                />
              </Space>
            }
          >
            <Checkbox
              checked={rateLimitEnabled}
              onChange={handleRateLimitEnabledChange}
              disabled={loading}
            >
              {t(
                "superAdmin.crawlConfig.enableHttpCrawlRateLimiting",
                "Enable HTTP crawl rate limiting",
              )}
            </Checkbox>
          </Form.Item>
          <Form.Item
            label={
              <Space align="center">
                <span>
                  {t(
                    "superAdmin.crawlConfig.crawlHttpRateLimit",
                    "Crawl HTTP Rate Limit",
                  )}
                </span>
                <InfoModal
                  label="Crawl HTTP Rate Limit"
                  info={t(
                    "superAdmin.crawlConfig.crawlHttpRateLimitInfo",
                    "Set the maximum HTTP crawl request rate. This is required when HTTP rate limiting is enabled.",
                  )}
                />
              </Space>
            }
            required={rateLimitEnabled}
          >
            <InputNumber
              min={0}
              step={0.1}
              value={crawlHttpRateLimit}
              onChange={setCrawlHttpRateLimit}
              style={{ width: "100%" }}
              disabled={!rateLimitEnabled || loading}
              placeholder="e.g. 1.5"
            />
          </Form.Item>
          <Title level={5} style={{ marginBottom: 8 }}>
            <Space align="center">
              <span>
                {t("superAdmin.crawlConfig.crawlRulesTitle", "Crawl Rules")}
              </span>
              <InfoModal
                label="Crawl Rules"
                info={t(
                  "superAdmin.crawlConfig.crawlRulesInfo",
                  "Rules to allow or deny crawling of URLs based on patterns. Each rule has a policy (allow/deny), type (begins/ends/contains/regex), and a pattern.",
                )}
              />
            </Space>
          </Title>
          {domain.crawl_rules.map((rule, j) => (
            <div className="crawl-rule-row" key={j}>
              {j > 0 && <Divider style={{ margin: "12px 0" }} />}
              <div className="crawl-rule-label">
                {t("superAdmin.crawlConfig.policy", "Policy")}
                <InfoModal
                  label="Policy"
                  info={t(
                    "superAdmin.crawlConfig.policyInfo",
                    "Choose 'allow' to permit crawling URLs matching the pattern, 'deny' to block them, 'allow all' to allow everything, or 'deny all' to block everything.",
                  )}
                />
              </div>
              <Select
                className="crawl-rule-field"
                value={rule.policy}
                onChange={(val) => handleCrawlRuleChange(j, "policy", val)}
                style={{ width: 120 }}
                disabled={loading}
              >
                <Option value="allow all">allow all</Option>
                <Option value="allow">allow</Option>
                <Option value="deny all">deny all</Option>
                <Option value="deny">deny</Option>
              </Select>
              <div className="crawl-rule-label">
                {t("superAdmin.crawlConfig.type", "Type")}
                <InfoModal
                  label="Type"
                  info={t(
                    "superAdmin.crawlConfig.typeInfo",
                    "Select how the pattern should be matched: begins, ends, contains, or regex.",
                  )}
                />
              </div>
              <Select
                className="crawl-rule-field"
                value={rule.type}
                onChange={(val) => handleCrawlRuleChange(j, "type", val)}
                style={{ width: 110 }}
                disabled={
                  rule.policy === "allow all" ||
                  rule.policy === "deny all" ||
                  loading
                }
              >
                <Option value="begins">begins</Option>
                <Option value="ends">ends</Option>
                <Option value="contains">contains</Option>
                <Option value="regex">regex</Option>
              </Select>
              <div className="crawl-rule-label">
                {t("superAdmin.crawlConfig.pattern", "Pattern")}
                <InfoModal
                  label="Pattern"
                  info={t(
                    "superAdmin.crawlConfig.patternInfo",
                    "Enter the string or regex pattern to match URL paths for this rule.",
                  )}
                />
              </div>
              <Input
                className="crawl-rule-pattern"
                value={rule.pattern}
                onChange={(e) =>
                  handleCrawlRuleChange(j, "pattern", e.target.value)
                }
                placeholder={
                  rule.policy === "allow all" || rule.policy === "deny all"
                    ? ".*"
                    : rule.type === "contains"
                      ? "URL path contains this"
                      : rule.type === "begins"
                        ? "URL path begins with this"
                        : rule.type === "ends"
                          ? "URL path ends with this"
                          : rule.type === "regex"
                            ? "Enter a regex pattern"
                            : "Pattern"
                }
                disabled={
                  rule.policy === "allow all" ||
                  rule.policy === "deny all" ||
                  loading
                }
              />
              <div className="crawl-rule-remove">
                <Button
                  danger
                  type="default"
                  icon={<Trash2 size={18} />}
                  onClick={() => removeCrawlRule(j)}
                  disabled={loading}
                >
                  {t("superAdmin.crawlConfig.removeRule", "Remove Rule")}
                </Button>
              </div>
            </div>
          ))}
          <Form.Item>
            <Button
              type="dashed"
              icon={<Plus size={18} />}
              onClick={addCrawlRule}
              style={{ width: "100%", top: 12 }}
              disabled={loading}
            >
              {t(
                "superAdmin.crawlConfig.addAnotherCrawlRule",
                "Add Another Crawl Rule",
              )}
            </Button>
          </Form.Item>
          {error && <div style={{ color: "red", marginTop: 16 }}>{error}</div>}
        </Card>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          style={{ backgroundColor: "#007b8f", color: "#BBE802" }}
          block
          disabled={loading}
        >
          {loading
            ? isEdit
              ? t("common.editing", "Updating...")
              : t("common.adding", "Creating...")
            : isEdit
              ? t("superAdmin.crawlConfig.updateCrawl", "Update Crawl")
              : t("superAdmin.crawlConfig.createCrawl", "Create Crawl")}
        </Button>
      </Form>
    </>
  );
};

CrawlConfig.propTypes = {
  clientIdsWithConfig: PropTypes.array.isRequired,
  closeModal: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  editConfig: PropTypes.object,
};

export default CrawlConfig;
